import { Check, MessageSquare, Phone } from 'lucide-react'
import type { ReactElement } from 'react'
import { Button } from 'app/components/ui/button'
import { intercomEnabled as isIntercomEnabled } from 'app/intercom'
import { useWorkspace } from 'app/utils'
export type AgentKind = `voice` | `sms`

const AGENT_NAME: Record<AgentKind, string> = { voice: `Voice Agent`, sms: `SMS Agent` }

const CAPABILITIES: Record<AgentKind, string[]> = {
  voice: [
    `Answers after-hours and missed calls`,
    `Books new and existing patients`,
    `Transfers emergencies to your team`,
    `Turns everything else into a task`,
  ],
  sms: [
    `Runs recall and confirmation outreach`,
    `Follows up with new leads in minutes`,
    `Books and confirms from your schedule`,
    `Hands questions to your inbox`,
  ],
}

const HOW_IT_WORKS: Record<AgentKind, string[]> = {
  voice: [
    `Forward missed or all calls to your agent's number.`,
    `It answers, checks your live schedule, and books the appointment.`,
    `Anything it can't handle goes to your team with a summary.`,
  ],
  sms: [
    `Pick who to reach: overdue recalls, unconfirmed appointments, new leads.`,
    `The agent texts them and handles the replies.`,
    `Bookings land in your schedule; anything else goes to your team.`,
  ],
}

const AGENT_ICON = { voice: Phone, sms: MessageSquare } as const

const AGENT_PITCH: Record<AgentKind, string> = {
  voice: `Answers calls 24/7 and books appointments.`,
  sms: `Texts patients 24/7 and books appointments.`,
}

/** The ask for an agent the practice doesn't have: Intercom opens with the request written. */
function AgentAskButton({
  kind,
  size,
  intercomEnabled,
}: {
  kind: AgentKind
  size?: `sm`
  intercomEnabled: boolean
}): ReactElement {
  const workspace = useWorkspace()
  const name = AGENT_NAME[kind]
  if (!intercomEnabled) return <span className="text-body-xs text-subtle">Ask your Trackable rep to add it.</span>
  return (
    <Button
      size={size}
      onClick={() =>
        window.Intercom?.(
          `showNewMessage`,
          `Hi! I'd like to add the ${name}${workspace?.name ? ` to ${workspace.name}` : ``}.`,
        )
      }
    >
      Ask About the {name}
    </Button>
  )
}

/** A customer's page for an agent their practice doesn't have yet: what it does, and how to ask for it. */
export function AgentAskCard({
  kind,
  intercomEnabled = isIntercomEnabled(),
}: {
  kind: AgentKind
  intercomEnabled?: boolean
}): ReactElement {
  const Icon = AGENT_ICON[kind]
  return (
    <section className="overflow-hidden rounded-xl border border-default bg-surface">
      <div className="flex flex-col gap-6 p-6 md:flex-row md:gap-10">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-muted">
            <Icon className="h-5 w-5 text-default" />
          </span>
          <h2 className="text-heading-lg text-strong">{AGENT_NAME[kind]}</h2>
          <p className="text-body-md text-subtle">{AGENT_PITCH[kind]}</p>
          <ol className="mt-2 flex flex-col gap-2">
            {HOW_IT_WORKS[kind].map((step, index) => (
              <li key={step} className="flex gap-3 text-body-sm text-default">
                <span className="w-4 shrink-0 text-label-sm tabular-nums text-minimal">{index + 1}</span>
                {step}
              </li>
            ))}
          </ol>
        </div>
        <div className="flex flex-col gap-2 md:w-72">
          <div className="text-label-sm text-subtle">What it handles</div>
          <ul className="flex flex-col gap-2">
            {CAPABILITIES[kind].map((line) => (
              <li key={line} className="flex items-center gap-2.5 text-body-sm text-default">
                <Check className="h-4 w-4 shrink-0 text-success-default" /> {line}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-default bg-surface-subtle px-6 py-3">
        <span className="text-body-xs text-subtle">Not on your plan yet</span>
        <AgentAskButton kind={kind} size="sm" intercomEnabled={intercomEnabled} />
      </div>
    </section>
  )
}
